import { useEffect, useRef } from "react";
import { Eraser, PenLine } from "lucide-react";

export function SignaturePad({ value = "", onChange, height = 150 }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const ctxOf = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0A2540";
    return ctx;
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = w * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.clearRect(0, 0, w, height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, w, height);
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = ctxOf();
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ctxOf();
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) onChange?.(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    onChange?.("");
  };

  return (
    <div data-testid="signature-pad">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500"><PenLine size={13} className="text-ciel" /> Signature manuscrite du médecin</span>
        <button type="button" onClick={clear} data-testid="signature-clear" className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 hover:text-red-500">
          <Eraser size={12} /> Effacer
        </button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        style={{ height, touchAction: "none" }}
        className="mt-1 w-full cursor-crosshair rounded-lg border border-dashed border-slate-300 bg-white"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <p className="mt-1 text-[11px] text-slate-400">Signez ci-dessus à la souris ou au doigt — la signature apparaîtra sur le compte-rendu PDF et Word.</p>
    </div>
  );
}
